import type { CampaignDuration } from "@/types/campaign";

export type CampaignLifecycleStatus = "active" | "ended" | "upcoming";

export const getCampaignLifecycleStatus = (
  duration: CampaignDuration,
  now: Date
): CampaignLifecycleStatus => {
  if (now > new Date(duration.end_date)) return "ended";
  if (now < new Date(duration.start_date)) return "upcoming";
  return "active";
};

// Campaign dates are absolute UTC instants — format them in the campaign's
// own zone_name so the displayed day doesn't shift with the viewer's locale.
// Intl.DateTimeFormat construction (with a timeZone) is expensive enough that
// building fresh instances per campaign, per render, is noticeable once a
// campaign list is large — so formatters are built once per zone and reused.
type CampaignDateFormatters = {
  year: Intl.DateTimeFormat;
  short: Intl.DateTimeFormat;
  withYear: Intl.DateTimeFormat;
};

const formattersByZone = new Map<string, CampaignDateFormatters>();

const getFormatters = (zoneName: string): CampaignDateFormatters => {
  const cached = formattersByZone.get(zoneName);
  if (cached) return cached;

  const formatters: CampaignDateFormatters = {
    year: new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      timeZone: zoneName,
    }),
    short: new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      timeZone: zoneName,
    }),
    withYear: new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: zoneName,
    }),
  };
  formattersByZone.set(zoneName, formatters);
  return formatters;
};

export const formatCampaignDateRange = (
  duration: CampaignDuration,
  zoneName: string
) => {
  const start = new Date(duration.start_date);
  const end = new Date(duration.end_date);
  const { year, short, withYear } = getFormatters(zoneName);
  const sameYear = year.format(start) === year.format(end);

  const startLabel = sameYear ? short.format(start) : withYear.format(start);

  return `${startLabel} – ${withYear.format(end)}`;
};
